/**
 * @fileoverview `<PasswordInput>` — champ mot de passe avec révélation contrôlée.
 *
 * Le backoffice utilise e-mail + mot de passe puis MFA. Le champ doit rester
 * homogène avec `<Input>` tout en offrant une action icon-only claire pour
 * afficher/masquer la valeur sans recréer ce pattern dans chaque formulaire.
 */

import * as React from 'react';
import { Eye, EyeOff, KeyRound } from 'lucide-react';
import { Button } from './ui/button.tsx';
import { Input, type InputProps } from './ui/input.tsx';
import { cn } from '../lib/utils.ts';

export interface PasswordInputProps extends Omit<InputProps, 'type' | 'leftIcon' | 'rightIcon'> {
  /** Libellé accessible quand le mot de passe est masqué. */
  revealLabel?: string;
  /** Libellé accessible quand le mot de passe est visible. */
  hideLabel?: string;
}

/**
 * Champ mot de passe standard.
 *
 * @param props - Props d'input GNPC et libellés accessibles du toggle.
 * @returns Input typé password/text avec bouton de révélation.
 *
 * @example
 * ```tsx
 * <PasswordInput minLength={12} autoComplete="current-password" />
 * ```
 */
export const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput(
    {
      className,
      revealLabel = 'Afficher le mot de passe',
      hideLabel = 'Masquer le mot de passe',
      ...props
    },
    ref,
  ) {
    const [visible, setVisible] = React.useState(false);

    return (
      <div className="relative w-full">
        <Input
          ref={ref}
          type={visible ? 'text' : 'password'}
          leftIcon={<KeyRound />}
          className={cn('pr-10', className)}
          {...props}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="absolute right-1.5 top-1/2 -translate-y-1/2"
          onClick={() => setVisible((current) => !current)}
          aria-label={visible ? hideLabel : revealLabel}
        >
          {visible ? <EyeOff /> : <Eye />}
        </Button>
      </div>
    );
  },
);
